/**
 * Global toast notification system.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success("Saved!");
 *   toast.error("Couldn't save.");
 *   toast({ title: "Heads up", body: "Optional details", tone: "info", duration: 6000 });
 *
 * The provider mounts a portal stack in the corner. Toasts auto-dismiss
 * after `duration` ms (default 4000) with a thin progress bar.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from "lucide-react";

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastInput {
  title: string;
  body?: string;
  tone?: ToastTone;
  duration?: number;
}

interface InternalToast extends ToastInput {
  id: string;
  exiting?: boolean;
}

interface ToastApi {
  toast: ((input: ToastInput | string) => void) & {
    success: (title: string, body?: string) => void;
    error:   (title: string, body?: string) => void;
    info:    (title: string, body?: string) => void;
    warning: (title: string, body?: string) => void;
  };
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_META: Record<ToastTone, { color: string; bg: string; icon: React.ReactNode }> = {
  info:    { color: "var(--status-info)",    bg: "var(--status-info-bg)",    icon: <Info        size={16} strokeWidth={1.8} /> },
  success: { color: "var(--status-success)", bg: "var(--status-success-bg)", icon: <CheckCircle2 size={16} strokeWidth={1.8} /> },
  warning: { color: "var(--status-warning)", bg: "var(--status-warning-bg)", icon: <AlertTriangle size={16} strokeWidth={1.8} /> },
  danger:  { color: "var(--status-danger)",  bg: "var(--status-danger-bg)",  icon: <AlertCircle  size={16} strokeWidth={1.8} /> },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<InternalToast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 420);
  }, []);

  const push = useCallback((input: ToastInput | string) => {
    const data: InternalToast = typeof input === "string"
      ? { id: String(++idRef.current), title: input, tone: "info", duration: 4000 }
      : { id: String(++idRef.current), tone: "info", duration: 4000, ...input };
    setToasts((prev) => [...prev, data]);
    if (data.duration && data.duration > 0) {
      setTimeout(() => dismiss(data.id), data.duration);
    }
  }, [dismiss]);

  const toast = push as ToastApi["toast"];
  toast.success = (title, body) => push({ title, body, tone: "success" });
  toast.error   = (title, body) => push({ title, body, tone: "danger"  });
  toast.info    = (title, body) => push({ title, body, tone: "info"    });
  toast.warning = (title, body) => push({ title, body, tone: "warning" });

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: "var(--z-toast)" as unknown as number,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            pointerEvents: "none",
            maxWidth: 380,
          }}
        >
          {toasts.map((t) => {
            const meta = TONE_META[t.tone || "info"];
            return (
              <div
                key={t.id}
                className={t.exiting ? "notif-toast-out" : "notif-toast-in"}
                style={{
                  pointerEvents: "auto",
                  background: "var(--bg-surface-raised)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-modal)",
                  padding: "12px 14px",
                  display: "flex",
                  gap: 10,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <span style={{
                  width: 28, height: 28, flexShrink: 0,
                  borderRadius: "var(--radius-md)",
                  background: meta.bg,
                  color: meta.color,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>
                  {meta.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: "var(--font-size-sm)", fontWeight: 600,
                    color: "var(--text-primary)", lineHeight: 1.3,
                  }}>
                    {t.title}
                  </div>
                  {t.body && (
                    <div style={{
                      fontSize: "var(--font-size-xs)", color: "var(--text-secondary)",
                      marginTop: 2, lineHeight: 1.4,
                    }}>
                      {t.body}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  style={{
                    width: 24, height: 24, flexShrink: 0,
                    background: "transparent", border: "none",
                    color: "var(--text-tertiary)", cursor: "pointer",
                    borderRadius: "var(--radius-sm)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <X size={12} strokeWidth={1.5} />
                </button>
                {t.duration && t.duration > 0 && (
                  <span
                    aria-hidden
                    className="notif-toast-progress"
                    style={{
                      position: "absolute", left: 0, bottom: 0,
                      height: 2, width: "100%",
                      background: meta.color,
                      animationDuration: `${t.duration}ms`,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
